import { TeaseStyle } from "design-system/types/types";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { TeaseFeedArticlesEntry } from "frontend/contentful/schema/blocks";
import { createComponentHeaderProps } from "frontend/contentful/components/primitives/component-header.props";
import { createUseSearchProps } from "frontend/contentful/components/utils/use-search.props";
import { useSearch } from "frontend/hooks/use-search";
import {
  createModernSearchArticleTease,
  createSearchArticleTease,
} from "frontend/contentful/search/search-article-tease";
import { LegacySearchHit, ModernSearchHit } from "frontend/types";
import { createArticleTeaseProps } from "frontend/contentful/components/blocks/teases/article-tease.props";
import { TeaseFeed } from "design-system/components/blocks/tease-feed/tease-feed";
import { LazyTeaseFeedTease } from "design-system/components/blocks/tease-feed/tease-feed-tease";
import { usePageProps } from "frontend/hooks/use-page-props";
import { useContentful } from "frontend/hooks/use-contentful";
import { getSiteId } from "frontend/lib/get-site-id";

interface TeaseFeedArticleOptions {
  headerAbove?: boolean;
}

/**
 * Querying ElasticSearch records with curated list of IDs.
 */
const CuratedContentfulTeaseFeedArticles = ({
  entry,
  options,
}: {
  entry: TeaseFeedArticlesEntry;
  options?: TeaseFeedArticleOptions;
}) => {
  const page = usePageProps();
  const { getEntry } = useContentful();
  const site = getEntry(page.site);
  const siteId = getSiteId(site);
  const ids = entry.fields.teasesCurated || [];
  const teases: Array<LazyTeaseFeedTease> = [];
  const headerAbove = options?.headerAbove || false;

  const { data } = useSearch<ModernSearchHit>({
    index: "modern",
    query: ids.map((id) => `id:${id}`).join(" OR "),
    size: entry.fields.teasesCurated?.length || 25,
  });

  /**
   * Sorting hits based on the order of IDs.
   *
   * TODO: Consider updating /api/search/query to accept
   * custom sorting algorithm
   */
  const sortedHits = data?.hits.toSorted((a, b) => {
    return ids.indexOf(a.id) - ids.indexOf(b.id);
  });

  const headerContent = createComponentHeaderProps(entry.fields.header);

  if (!sortedHits || sortedHits.length === 0) return null;

  sortedHits.forEach((item) => {
    const props = createModernSearchArticleTease(item, siteId, {
      pageProps: page,
    });
    if (props) teases.push({ name: "ArticleTease", props });
  });

  return (
    <TeaseFeed
      teaseStyle={entry.fields.teaseStyle as TeaseStyle}
      align={entry.fields.alignment}
      headerContent={headerContent}
      items={teases}
      HeadingLevel={headerAbove ? "h3" : "h2"}
      HeadingLevelTease={
        headerAbove && headerContent
          ? "h4"
          : headerAbove || headerContent
            ? "h3"
            : "h2"
      }
    />
  );
};

/**
 * Querying ElasticSearch records with provided query.
 */
const QueryContentfulTeaseFeedArticles = ({
  entry,
  options,
}: {
  entry: TeaseFeedArticlesEntry;
  options?: TeaseFeedArticleOptions;
}) => {
  const page = usePageProps();
  const { getEntry } = useContentful();
  const site = getEntry(page.site);
  const siteId = getSiteId(site);
  const headerAbove = options?.headerAbove || false;
  const headerContent = createComponentHeaderProps(entry.fields.header);

  const searchProps = createUseSearchProps(entry.fields.teasesQuery);
  const { data } = useSearch<LegacySearchHit>(searchProps);

  const teases: Array<LazyTeaseFeedTease> = [];

  data?.hits.forEach((item) => {
    const props = createSearchArticleTease(item, siteId, { pageProps: page });
    if (props) teases.push({ name: "ArticleTease", props });
  });

  return (
    <TeaseFeed
      teaseStyle={entry.fields.teaseStyle as TeaseStyle}
      align={entry.fields.alignment}
      headerContent={headerContent}
      items={teases}
      HeadingLevel={headerAbove ? "h3" : "h2"}
      HeadingLevelTease={
        headerAbove && headerContent
          ? "h4"
          : headerAbove || headerContent
            ? "h3"
            : "h2"
      }
    />
  );
};

/**
 * Deprecated. Choosing Contentful entries manually.
 */
const ManualContentfulTeaseFeedArticles = ({
  entry,
  options,
}: {
  entry: TeaseFeedArticlesEntry;
  options?: TeaseFeedArticleOptions;
}) => {
  const teases: Array<LazyTeaseFeedTease> = [];
  const headerAbove = options?.headerAbove || false;
  const headerContent = createComponentHeaderProps(entry.fields.header);

  entry.fields.teasesManual?.forEach((tease) => {
    const props = createArticleTeaseProps(tease);
    if (props) teases.push({ name: "ArticleTease", props });
  });

  return (
    <TeaseFeed
      teaseStyle={entry.fields.teaseStyle as TeaseStyle}
      align={entry.fields.alignment}
      headerContent={headerContent}
      HeadingLevel={headerAbove ? "h3" : "h2"}
      HeadingLevelTease={
        headerAbove && headerContent
          ? "h4"
          : headerAbove || headerContent
            ? "h3"
            : "h2"
      }
      items={teases}
    />
  );
};

export const ContentfulTeaseFeedArticles = createContentfulComponent<
  TeaseFeedArticlesEntry,
  TeaseFeedArticleOptions
>(({ entry, options }) => {
  if (entry.fields.teasesCurated && entry.fields.teasesCurated.length > 0) {
    return (
      <CuratedContentfulTeaseFeedArticles entry={entry} options={options} />
    );
  }

  if (entry.fields.teasesQuery) {
    return <QueryContentfulTeaseFeedArticles entry={entry} options={options} />;
  }

  // Deprecated
  if (entry.fields.teasesManual && entry.fields.teasesManual.length > 0) {
    return (
      <ManualContentfulTeaseFeedArticles entry={entry} options={options} />
    );
  }

  return null;
});
